import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import Footer from '../../Footer/Footer';
import BaseUrl from '../../ApiServices/BaseUrl';
import Swal from 'sweetalert2';
import './JobSeekerMessages.css';

function JobSeekerMessages() {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;

    const locationState = window.history.state?.usr?.selectedConversation || null;
    const [allMessages, setAllMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedConversation, setSelectedConversation] = useState(locationState);
    const [messageContent, setMessageContent] = useState('');
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    // Guided Chat
    const [chatStatus, setChatStatus] = useState({ stepCount: 0, isLocked: false, nextSenderIsCandidate: true });
    const [candidateSubPlan, setCandidateSubPlan] = useState('free');
    const [allowFreeCustomChat, setAllowFreeCustomChat] = useState(false);
    const [canSendMessage, setCanSendMessage] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [inputSalary, setInputSalary] = useState('');
    const [sending, setSending] = useState(false);
    const [employerHasSub, setEmployerHasSub] = useState(false);

    const isMobile = screenWidth < 768;

    const isSameConversation = (a, b) => {
        if (!a || !b) return false;
        const aJobId = a.job_id ?? null;
        const bJobId = b.job_id ?? null;
        return String(a.emp_id) === String(b.emp_id) && String(aJobId) === String(bJobId);
    };

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch all data
    useEffect(() => {
        if (!userId) return;

        const fetchAll = async () => {
            setLoading(true);
            try {
                const [msgRes, planRes, adminRes, profileRes] = await Promise.all([
                    fetch(`${BaseUrl}/message/conversations/jobseeker/${userId}`),
                    fetch(`${BaseUrl}/api/candidate/subscription/${userId}`),
                    fetch(`${BaseUrl}/api/admin/settings/allow_free_custom_chat`),
                    fetch(`${BaseUrl}/api/job-seeker/${userId}`)
                ]);

                if (msgRes.ok) {
                    const data = await msgRes.json();
                    setAllMessages(data);
                    
                    // If we navigated here with a selected conv, we might want to refresh status
                    // using handleSelectConversation on the newly fetched full conversation object
                    if (locationState && data.length > 0) {
                        const key = `${locationState.job_id}_${locationState.emp_id}`;
                        // find if this key is in the constructed conversations later...
                        // We'll let a separate effect handle the initial select to ensure we have the grouped conversation structure
                    }
                }

                if (planRes.ok) {
                    const planData = await planRes.json();
                    if (planData?.subscription?.plan_type) {
                        setCandidateSubPlan(planData.subscription.plan_type.toLowerCase());
                    }
                }

                if (adminRes.ok) {
                    const adminData = await adminRes.json();
                    setAllowFreeCustomChat(adminData.is_enabled === 1);
                }

                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    setUserProfile(profileData);
                }
            } catch (err) {
                console.error('Error fetching messages:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();

        // Polling logic for real-time messages
        const pollInterval = setInterval(() => {
            fetch(`${BaseUrl}/message/conversations/jobseeker/${userId}`)
                .then(res => {
                    if (res.ok) return res.json();
                    throw new Error('Network response was not ok');
                })
                .then(data => setAllMessages(data))
                .catch(err => console.error('Error polling messages:', err));
        }, 5000); // 5 seconds polling

        return () => clearInterval(pollInterval); // Cleanup on unmount
    }, [userId]);

    // Group messages by (job_id + emp_id) to form conversations
    const conversationsMap = {};
    allMessages.forEach(msg => {
        const key = `${msg.job_id}_${msg.emp_id}`;
        if (!conversationsMap[key]) {
            conversationsMap[key] = {
                key,
                job_id: msg.job_id,
                emp_id: msg.emp_id,
                job_title: msg.job_title || (msg.job_id ? `Job #${msg.job_id}` : (msg.agency_name || msg.emp_org_name || 'Employer')),
                working_city: msg.working_city,
                country: msg.country,
                emp_name: msg.agency_name || msg.emp_org_name || ((msg.emp_first_name || '') + ' ' + (msg.emp_last_name || '')).trim() || 'Employer',
                job_profile_photo: (!msg.job_id && (msg.agency_photo || msg.emp_photo)) 
                    ? ((msg.agency_photo || msg.emp_photo).startsWith('/') ? (msg.agency_photo || msg.emp_photo) : '/' + (msg.agency_photo || msg.emp_photo).replace(/\\/g, '/'))
                    : msg.job_profile_photo,
                post_by: msg.post_by,
                messages: [],
                lastMessageTime: msg.created_at,
                has_agency_info: !!(msg.agency_name || msg.emp_org_name)
            };
        } else if (!conversationsMap[key].has_agency_info && (msg.agency_name || msg.emp_org_name)) {
            // Robust fallback: if first message lacked info, grab it from any later message
            conversationsMap[key].emp_name = msg.agency_name || msg.emp_org_name;
            if (!msg.job_id && (msg.agency_photo || msg.emp_photo)) {
                const photo = msg.agency_photo || msg.emp_photo;
                conversationsMap[key].job_profile_photo = (photo.startsWith('/') ? photo : '/' + photo.replace(/\\/g, '/'));
            }
            conversationsMap[key].has_agency_info = true;
        }

        conversationsMap[key].messages.push(msg);
        if (new Date(msg.created_at) > new Date(conversationsMap[key].lastMessageTime)) {
            conversationsMap[key].lastMessageTime = msg.created_at;
        }
    });

    const conversations = Object.values(conversationsMap).sort(
        (a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
    );

    const getUnreadCountForConversation = (conv) => {
        if (!conv?.messages?.length) return 0;
        return conv.messages.filter((msg) => {
            const incoming = String(msg.sender_id) !== String(userId);
            const unread = !(Number(msg.is_read) === 1);
            return incoming && unread;
        }).length;
    };

    const totalUnreadCount = conversations.reduce((sum, conv) => sum + getUnreadCountForConversation(conv), 0);

    // Initial conversation selection effect if passed via navigation state
    useEffect(() => {
        if (locationState && conversations.length > 0 && !selectedConversation) {
            const initialConv = conversations.find(c => c.key === locationState.key);
            if (initialConv) {
                handleSelectConversation(initialConv);
            }
        }
    }, [locationState, conversations.length]);

    const currentConversationMessages = selectedConversation
        ? allMessages.filter(m => m.job_id === selectedConversation.job_id && m.emp_id === selectedConversation.emp_id)
        : [];

    const getSafeString = (value, fallback = '') => {
        if (value === null || value === undefined) return fallback;
        const str = String(value).trim();
        return str || fallback;
    };

    const getPrimaryJobPosition = () => {
        const positions = Array.isArray(userProfile?.job_position)
            ? userProfile.job_position
            : (typeof userProfile?.job_position === 'string' ? [userProfile.job_position] : []);
        if (positions.length > 0) return getSafeString(positions[0], 'Job Role');
        return getSafeString(selectedConversation?.job_title, 'Job Role');
    };

    const extractJobPositionOnly = () => {
        // Always prefer candidate's own profile position — never fall back to employer's job title
        const safeParseFirst = (val) => {
            if (!val) return null;
            if (Array.isArray(val)) return getSafeString(val[0], null);
            try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return getSafeString(parsed[0], null);
            } catch (e) { /* not json */ }
            return getSafeString(val, null);
        };
        const fromProfile =
            safeParseFirst(userProfile?.job_position) ||
            getSafeString(userProfile?.position, null) ||
            getSafeString(userProfile?.applied_position, null) ||
            getSafeString(userProfile?.preferred_position, null);
        if (fromProfile) return fromProfile;
        // Only parse employer job title as last resort
        const title = getSafeString(selectedConversation?.job_title, '');
        if (title) {
            const match = title.match(/looking\s+for\s+(?:a|an)?\s*(.+)$/i);
            if (match && match[1]) return getSafeString(match[1].replace(/[.]+$/, ''), 'Job Role');
            const dashMatch = title.match(/^([^-]+?)\s*-\s*.+$/);
            if (dashMatch && dashMatch[1]) return getSafeString(dashMatch[1].trim(), 'Job Role');
            return title;
        }
        return 'Job Role';
    };

    const getGuidedCandidateMessage = (stepCount, manualSalary = '') => {
        const firstName = getSafeString(userProfile?.first_name || user?.first_name, '');
        const lastName = getSafeString(userProfile?.last_name || user?.last_name, '');
        const candidateName = [firstName, lastName].filter(Boolean).join(' ') || 'Candidate';
        const nationality = getSafeString(userProfile?.nationality, 'my country');
        const experienceYears = getSafeString(
            userProfile?.total_work_experience ||
            userProfile?.experience_years ||
            userProfile?.work_experience ||
            userProfile?.years_of_experience,
            null
        ) || null;
        const expectedAmount = getSafeString(manualSalary, '[amount]');
        const jobTitle = extractJobPositionOnly();

        if (stepCount === 0) {
            return `Hello, I am ${candidateName} from ${nationality}. I am interested in the job opportunities you have posted. Kindly review my profile and let me know if it is suitable for your position.`;
        }
        if (stepCount === 2) {
            return `Thank you for your response. I look forward to your feedback. Please let me know if you need any additional information.`;
        }
        if (stepCount === 4) {
            const exp = experienceYears || '[your experience]';
            return `Thank you for your response.\nI have ${exp} of experience as ${jobTitle}.\nMy expected salary is ${expectedAmount}.\nI am available for interview at your convenience.`;
        }
        if (stepCount === 6) {
            return `Please call me at your convenience. Thank you.`;
        }
        return '';
    };

    const applyTemplates = (statusData, isEmpSub = employerHasSub) => {
        const enforceGuided = candidateSubPlan === 'free' && !allowFreeCustomChat && !isEmpSub;

        if (enforceGuided && statusData) {
            if (statusData.isLocked) {
                // Don't set message content — keep field empty, popup handles it
                setCanSendMessage(false);
            } else if (!statusData.nextSenderIsCandidate) {
                // Don't reset messageContent — user may be typing in the textarea
                setCanSendMessage(false);
            } else {
                const wasCandidate = canSendMessage; // already candidate's turn = no change needed
                setCanSendMessage(true);
                // Only re-generate when the turn just switched to candidate's turn
                if (!wasCandidate) {
                    setMessageContent(getGuidedCandidateMessage(statusData.stepCount, inputSalary));
                }
            }
        } else {
            setCanSendMessage(true);
            setMessageContent('');
        }
    };

    useEffect(() => {
        const enforceGuided = candidateSubPlan === 'free' && !allowFreeCustomChat && !employerHasSub;
        if (!enforceGuided || !selectedConversation || !chatStatus.nextSenderIsCandidate || chatStatus.isLocked) return;
        const template = getGuidedCandidateMessage(chatStatus.stepCount, inputSalary);
        if (template && (!messageContent || chatStatus.stepCount !== 4)) {
            setMessageContent(template);
        }
    }, [inputSalary, selectedConversation, chatStatus.stepCount, chatStatus.nextSenderIsCandidate, chatStatus.isLocked, candidateSubPlan, allowFreeCustomChat, employerHasSub]);

    const fetchConversationStatus = async (jobId, isEmpSub = employerHasSub) => {
        try {
            const statusRes = await fetch(`${BaseUrl}/message/conversation-status?job_id=${jobId}&job_seeker_id=${userId}`);
            if (statusRes.ok) {
                const statusData = await statusRes.json();
                setChatStatus(statusData);
                applyTemplates(statusData, isEmpSub);
                return statusData;
            }
        } catch (e) {
            console.error('Error fetching chat status:', e);
        }
        return null;
    };

    const handleSelectConversation = async (conv) => {
        setSelectedConversation(conv);
        setMessageContent('');
        setInputSalary('');
        setEmployerHasSub(false); // Reset
        await markConversationAsRead(conv);

        const statusData = await fetchConversationStatus(conv.job_id);

        // Fetch employer/agency subscription
        let empHasSub = false;
        try {
            const isAgency = conv.post_by && conv.post_by.toLowerCase() === 'agency';
            const subApiRoute = isAgency ? `/api/agency/subscription/${conv.emp_id}` : `/api/employer/subscription/${conv.emp_id}`;

            const empSubRes = await fetch(`${BaseUrl}${subApiRoute}`);
            if (empSubRes.ok) {
                const empSubData = await empSubRes.json();
                const sub = empSubData.subscription || {};
                const planName = String(sub.plan_type || sub.plan_name || sub.name || '').toLowerCase();
                const isPaid = planName !== '' && !planName.includes('free');
                empHasSub = sub.status === 'active' && isPaid;
                setEmployerHasSub(empHasSub);
            }
        } catch (e) {
            console.error('Error fetching job poster sub:', e);
        }

        const enforceGuided = candidateSubPlan === 'free' && !allowFreeCustomChat && !empHasSub;
        // The fetchConversationStatus above doesn't have the updated empHasSub in its closure yet
        // So we just call applyTemplates directly here using the newly fetched empHasSub
        applyTemplates(statusData, empHasSub);
    };

    useEffect(() => {
        if (!selectedConversation || allMessages.length === 0) return;
        const latestConv = conversations.find(c => isSameConversation(c, selectedConversation));
        if (latestConv) {
            markConversationAsRead(latestConv);
        }
    }, [allMessages, selectedConversation]);

    const refreshMessages = async () => {
        try {
            const res = await fetch(`${BaseUrl}/message/conversations/jobseeker/${userId}`);
            if (res.ok) {
                const data = await res.json();
                setAllMessages(data);
            }
        } catch (err) {
            console.error('Error refreshing messages:', err);
        }
    };

    const markConversationAsRead = async (conv) => {
        if (!conv || !userId) return;
        try {
            const res = await fetch(`${BaseUrl}/message/mark-read-jobseeker`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: conv.emp_id,
                    job_seeker_id: userId,
                    job_id: conv.job_id ?? null
                })
            });

            if (!res.ok) return;

            setAllMessages(prev =>
                prev.map(msg => {
                    const sameEmp = String(msg.emp_id) === String(conv.emp_id);
                    const sameJob = String(msg.job_id ?? null) === String(conv.job_id ?? null);
                    const incoming = String(msg.sender_id) !== String(userId);
                    if (sameEmp && sameJob && incoming) {
                        return { ...msg, is_read: 1 };
                    }
                    return msg;
                })
            );
        } catch (err) {
            console.error('Error marking jobseeker conversation as read:', err);
        }
    };

    const handleContactClick = (type) => {
        if (!selectedConversation) return;

        // If candidate is on free plan, block and show upgrade message
        if (candidateSubPlan === 'free') {
            Swal.fire({
                title: 'Upgrade Required',
                text: 'You need an active subscription plan to view employer contact details.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'View Plans',
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/subscriptionplans');
                }
            });
            return;
        }

        // Get the last message to retrieve employer phone details
        const lastMsg = selectedConversation.messages[selectedConversation.messages.length - 1];

        if (type === 'call') {
            const phoneCode = lastMsg.emp_phone_code || '';
            const phone = lastMsg.emp_phone || '';
            if (!phone) {
                Swal.fire('Not Available', 'Employer has not provided a phone number.', 'info');
                return;
            }
            window.location.href = `tel:${phoneCode}${phone}`;

        } else if (type === 'whatsapp') {
            const waCode = lastMsg.emp_whatsapp_code || lastMsg.emp_phone_code || '';
            const waNum = lastMsg.emp_whatsapp || lastMsg.emp_phone || '';
            if (!waNum) {
                Swal.fire('Not Available', 'Employer has not provided a WhatsApp number.', 'info');
                return;
            }
            const fullNum = `${waCode}${waNum}`.replace(/\D/g, '');
            window.open(`https://wa.me/${fullNum}`, '_blank');
        }
    };

    const handleSendMessage = async () => {
        const candidateProfile = userProfile || user;
        if (!candidateProfile?.verification_status || String(candidateProfile.verification_status).toLowerCase() !== 'verified') {
            Swal.fire({
                icon: 'warning',
                title: 'Account Not Verified',
                text: 'After admin approval only you can send messages for jobs. Please wait for your account to be verified.'
            });
            return;
        }

        const enforceGuided = candidateSubPlan === 'free' && !allowFreeCustomChat && !employerHasSub;

        if (enforceGuided && (!canSendMessage || !chatStatus.nextSenderIsCandidate)) {
            Swal.fire({
                html: `
                  <div style="text-align:center;padding:10px 0">
                    <div style="font-size:3rem;margin-bottom:12px">📱💬</div>
                    <p style="font-size:1rem;font-weight:600;color:#333;line-height:1.5">
                      Wait for the employer's reply, or take a subscription to chat,
                      send a WhatsApp message, email, or call directly.
                    </p>
                  </div>`,
                showCancelButton: true,
                confirmButtonText: '<i class="bi bi-whatsapp"></i> Subscribe Now',
                cancelButtonText: '<i class="bi bi-chat-left-dots"></i> Wait for Reply',
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#6c757d',
                reverseButtons: false,
                showCloseButton: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.href = '/candidate-subscription';
                }
            });
            return;
        }

        let finalMessage = messageContent;

        if (enforceGuided) {
            if (chatStatus.stepCount === 4 && !inputSalary.trim()) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Expected Salary Required',
                    text: 'Please enter your expected salary amount before sending.'
                });
                return;
            }

            const guidedMessage = getGuidedCandidateMessage(chatStatus.stepCount, inputSalary);
            if (!guidedMessage) {
                Swal.fire({
                    html: `
                      <div style="text-align:center;padding:10px 0">
                        <div style="font-size:3rem;margin-bottom:12px">📱💬</div>
                        <p style="font-size:1rem;font-weight:600;color:#333;line-height:1.5">
                          Wait for the employer's reply, or take a subscription to chat,
                          send a WhatsApp message, email, or call directly.
                        </p>
                      </div>`,
                    showCancelButton: true,
                    confirmButtonText: '<i class="bi bi-whatsapp"></i> Subscribe Now',
                    cancelButtonText: '<i class="bi bi-chat-left-dots"></i> Wait for Reply',
                    confirmButtonColor: '#28a745',
                    cancelButtonColor: '#6c757d',
                    reverseButtons: false,
                    showCloseButton: true,
                }).then((result) => {
                    if (result.isConfirmed) { window.location.href = '/candidate-subscription'; }
                });
                return;
            }
            // Always use the server-regenerated guided message to avoid profile data mismatch
            finalMessage = guidedMessage;
        }

        if (!finalMessage?.trim() || !selectedConversation) {
            Swal.fire({ icon: 'warning', title: 'Oops...', text: 'Please enter a message' });
            return;
        }

        setSending(true);
        try {
            Swal.fire({ title: 'Sending message...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const profileLink = `${window.location.origin}/candidateview/${user.id}/${user.first_name}`;

            const storeResponse = await fetch(`${BaseUrl}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: selectedConversation.emp_id,
                    job_seeker_id: user.id,
                    sender_id: userId,
                    message: finalMessage,
                    sender_type: 'job_seeker',
                    profile_link: profileLink,
                    job_id: selectedConversation.job_id,
                    is_custom_chat: !(candidateSubPlan === 'free' && !allowFreeCustomChat && !employerHasSub)
                })
            });

            if (!storeResponse.ok) {
                const errorData = await storeResponse.json();
                Swal.fire({ icon: 'error', title: 'Error', text: errorData.error || 'Failed to send message' });
                return;
            }

            Swal.fire({ icon: 'success', title: 'Sent!', text: 'Your message has been sent.', timer: 1500, showConfirmButton: false });

            setMessageContent('');
            setInputSalary('');
            await refreshMessages();
            await fetchConversationStatus(selectedConversation.job_id);

        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send message. Please try again.' });
        } finally {
            setSending(false);
        }
    };

    const showConversationList = isMobile ? !selectedConversation : true;
    const showChat = isMobile ? !!selectedConversation : !!selectedConversation;

    return (
        <>
            <Navbar />
            <div className="jobseeker-messages-page" style={{ marginTop: '80px' }}>
                <div className="jobseeker-messages-container">
                    {loading ? (
                        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: '400px' }}>
                            <div className="spinner-border text-primary" role="status" />
                            <span className="ms-3 text-muted">Loading messages...</span>
                        </div>
                    ) : allMessages.length === 0 ? (
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 text-muted" style={{ minHeight: '400px' }}>
                            <i className="bi bi-inbox" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                            <p className="mt-3">No messages yet. Start messaging employers from job listings!</p>
                        </div>
                    ) : (
                        <div className="jobseeker-messages-layout">
                            {/* Left - Conversation List */}
                            {showConversationList && (
                                <div className="jobseeker-messages-conv-panel">
                                    <div className="panel-header-js">
                                        <i className="bi bi-chat-dots me-2 text-primary"></i>
                                        <strong>Messages</strong>
                                        <span className="badge bg-primary ms-2">{conversations.length}</span>
                                        {totalUnreadCount > 0 && (
                                            <span className="badge bg-danger ms-2">{totalUnreadCount}</span>
                                        )}
                                    </div>
                                    {conversations.map(conv => {
                                        const isSelected = selectedConversation?.key === conv.key;
                                        const lastMsg = conv.messages[conv.messages.length - 1];
                                        const unreadCount = getUnreadCountForConversation(conv);
                                        return (
                                            <div
                                                key={conv.key}
                                                className={`conv-item ${isSelected ? 'selected' : ''} ${unreadCount > 0 ? 'conv-item-unread' : ''}`}
                                                onClick={() => handleSelectConversation(conv)}
                                            >
                                                <div className="conv-avatar">
                                                    {conv.job_profile_photo ? (
                                                        <img src={`${BaseUrl}${conv.job_profile_photo}`} alt="J" />
                                                    ) : (
                                                        <i className="bi bi-building text-secondary"></i>
                                                    )}
                                                </div>
                                                <div className="conv-info">
                                                    <div className="conv-title">{conv.job_title}</div>
                                                    <div className="conv-employer">{conv.emp_name}</div>
                                                    {lastMsg && (
                                                        <div className="conv-last-msg">
                                                            {lastMsg.sender_id === userId ? 'You: ' : ''}
                                                            {lastMsg.message?.substring(0, 40)}{lastMsg.message?.length > 40 ? '...' : ''}
                                                        </div>
                                                    )}
                                                    {unreadCount > 0 && (
                                                        <div className="conv-unread-row">
                                                            <span className="conv-unread-tag">Unread</span>
                                                            <span className="conv-unread-count">{unreadCount}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="conv-time">
                                                    {new Date(conv.lastMessageTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Right - Chat */}
                            {showChat ? (
                                <div className="jobseeker-messages-chat-panel">
                                    {/* Chat Header */}
                                    <div className="js-chat-header">
                                        <div className="d-flex align-items-center">
                                            {isMobile && (
                                                <button className="btn btn-link link-dark p-0 me-3" onClick={() => setSelectedConversation(null)}>
                                                    <i className="bi bi-arrow-left fs-5"></i>
                                                </button>
                                            )}
                                            <div className="conv-avatar me-2">
                                                {selectedConversation?.job_profile_photo ? (
                                                    <img src={`${BaseUrl}${selectedConversation.job_profile_photo}`} alt="J" />
                                                ) : (
                                                    <i className="bi bi-building text-secondary"></i>
                                                )}
                                            </div>
                                            <div>
                                                <div className="fw-bold" style={{ fontSize: '0.95rem' }}>{selectedConversation?.emp_name}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{selectedConversation?.job_title}</div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                onClick={() => handleContactClick('call')}
                                                title="Call Employer"
                                            >
                                                <i className="bi bi-telephone-fill text-primary"></i>
                                            </button>
                                            <button
                                                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                onClick={() => handleContactClick('whatsapp')}
                                                title="WhatsApp Employer"
                                            >
                                                <i className="bi bi-whatsapp text-success"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div className="js-chat-messages">
                                        {currentConversationMessages.length > 0 ? (
                                            currentConversationMessages.map((msg, index) => {
                                                const isFromMe = msg.sender_id === userId;
                                                return (
                                                    <div key={index} style={{ display: 'flex', justifyContent: isFromMe ? 'flex-end' : 'flex-start' }}>
                                                        <div className={`js-chat-bubble ${isFromMe ? 'sent' : 'received'}`}>
                                                            <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#333' }}>
                                                                {msg.message}
                                                            </div>
                                                            <div className="js-chat-time">
                                                                {new Date(msg.created_at).toLocaleString('en-GB', {
                                                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                                                })}
                                                                {isFromMe ? <i className="bi bi-check2-all text-primary ms-1"></i> : <i className="bi bi-reply-fill ms-1"></i>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center text-muted mt-5">
                                                <i className="bi bi-chat-left-text" style={{ fontSize: '2rem' }}></i>
                                                <p className="mt-2">No previous messages. Start the conversation!</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reply Area */}
                                    <div className="js-chat-reply-box">
                                        <div className="d-flex flex-column gap-2">
                                            {candidateSubPlan === 'free' && employerHasSub && (
                                                <div className="alert alert-info py-1 px-2 mb-1 text-center" style={{ fontSize: '0.75rem', border: 'none', borderRadius: '4px' }}>
                                                    <i className="bi bi-info-circle me-1"></i> Custom chat unlocked by employer's subscription
                                                </div>
                                            )}

                                            {(candidateSubPlan === 'free' && !allowFreeCustomChat && !employerHasSub) ? (
                                                canSendMessage && !chatStatus.isLocked ? (
                                                    // Candidate's turn: show pre-made message (read-only)
                                                    <div className="p-3 border rounded bg-light" style={{ minHeight: '80px' }}>
                                                        {chatStatus.stepCount === 4 ? (
                                                            <div>
                                                                <p className="mb-2" style={{ fontSize: '0.9rem' }}>
                                                                    My expected salary is <input
                                                                        type="text"
                                                                        className="form-control form-control-sm d-inline-block"
                                                                        style={{ width: '120px' }}
                                                                        placeholder="e.g. 1000"
                                                                        value={inputSalary}
                                                                        maxLength={5}
                                                                        onChange={(e) => {
                                                                            const raw = e.target.value.replace(/\D/g, '').slice(0, 5);
                                                                            setInputSalary(raw);
                                                                            setMessageContent(getGuidedCandidateMessage(4, raw));
                                                                        }}
                                                                    />.
                                                                </p>
                                                                {!inputSalary.trim() && (
                                                                    <small className="text-danger fw-bold">Expected salary amount is required to send.</small>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem', color: '#555' }}>
                                                                {messageContent || 'Preparing your message...'}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    // Waiting for employer reply OR locked: active editable textarea
                                                    <div>
                                                        <textarea
                                                            className="form-control"
                                                            rows={3}
                                                            value={messageContent}
                                                            onChange={(e) => setMessageContent(e.target.value)}
                                                            placeholder={chatStatus.isLocked ? 'Type a message...' : 'Type a message... (waiting for employer reply)'}
                                                            style={{ resize: 'none', borderRadius: '8px', fontSize: '0.9rem' }}
                                                        />
                                                        <small className="text-muted mt-1 d-block">
                                                            <i className="bi bi-info-circle me-1"></i>
                                                            Sending a custom message requires a subscription. Wait for employer reply for next guided message.
                                                        </small>
                                                    </div>
                                                )
                                            ) : (
                                                <div className="d-flex gap-2 align-items-end">
                                                    <textarea
                                                        className="form-control"
                                                        rows={2}
                                                        value={messageContent}
                                                        onChange={(e) => setMessageContent(e.target.value)}
                                                        placeholder="Type your message here..."
                                                        style={{ resize: 'none', borderRadius: '8px', fontSize: '0.9rem' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Send Button */}
                                        <div className="d-flex justify-content-end mt-3 gap-2">
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={handleSendMessage}
                                                style={{ paddingLeft: '20px', paddingRight: '20px' }}
                                                disabled={
                                                    sending ||
                                                    !messageContent.trim() ||
                                                    ((candidateSubPlan === 'free' && !allowFreeCustomChat && !employerHasSub) && chatStatus.stepCount === 4 && canSendMessage && !chatStatus.isLocked && !inputSalary.trim())
                                                }
                                            >
                                                Send Message <i className="bi bi-send-fill ms-1" style={{ fontSize: '0.8rem' }}></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                !isMobile && (
                                    <div className="jobseeker-messages-chat-panel d-flex align-items-center justify-content-center text-muted">
                                        <div className="text-center">
                                            <i className="bi bi-chat-left-text" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                            <p className="mt-3">Select a conversation to view messages</p>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default JobSeekerMessages;







